import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { EffectiveDate } from '@app/enums/effective-date.enum';
import { TimeOffApprovalFlow } from '@app/models/time-off-v3/time-off-approval-flow.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffRequestRules } from '@app/models/time-off-v3/time-off-request-rules.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { AdvancedPolicyForm, PolicyForm } from '@app/modules/time-off-v3/forms';
import { NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import differenceBy from 'lodash-es/differenceBy';

@Component({
    templateUrl: './policy-update.template.html',
    styleUrls: ['./policy-update.style.scss'],
})
export class PolicyUpdateView implements OnInit {
    @ViewChild('advancePolicyForm') advancePolicyForm: AdvancedPolicyForm;
    @ViewChild('policyForm') policyForm: PolicyForm;
    @ViewChild('confirmation') confirmation: DeprecatedConfirmationDialogComponent;

    isLoading = false;
    hasCompletedInitialLoad = false;
    timeOffPolicy: TimeOffPolicy = new TimeOffPolicy();
    timeOffTypes: TimeOffType[] = [];
    timeOffApprovalFlows: TimeOffApprovalFlow[] = [];
    requestRules: TimeOffRequestRules;
    todayValue: string = EffectiveDate.today;
    dateHiredValue: string = EffectiveDate.hireDate;

    constructor(
        private notify: NotifyService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.timeOffPolicy = this.route.snapshot.data.models.get('policy');
        this.requestRules = this.timeOffPolicy.timeOffRequestRule;
    }

    get warningMessage(): string {
        return this.timeOffPolicy.effectiveDate === EffectiveDate.today
            ? 'time-off-v3.changesMadeAsOfTodayMayResult'
            : 'time-off-v3.changesWillBeBackDated';
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        await this.loadTimeOffTypes();
        await this.loadTimeOffApprovalFlows();

        this.timeOffPolicy.effectiveDate = EffectiveDate.today;

        this.isLoading = false;
        this.hasCompletedInitialLoad = true;
    }

    async onSave(): Promise<void> {
        if (!this.submitAndValidate(this.policyForm) || !this.submitAndValidate(this.advancePolicyForm)) {
            return;
        }

        const confirmed = await this.confirmation.show();

        if (!confirmed) {
            return;
        }

        this.isLoading = true;

        // Order of operation matters here! We have to take care of the advance settings first before touching the policy
        try {
            if (!this.timeOffPolicy.isUnlimited) {
                await this.saveAdvancedSettings();
            }

            if (this.timeOffPolicy.isDirty()) {
                await this.timeOffPolicy.save();
            }

            this.notify.success('time-off-v3.timeOffPolicyHasBeenUpdated');
            this.redirect();
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async saveAdvancedSettings(): Promise<void> {
        const requestRules: TimeOffRequestRules = this.advancePolicyForm.requestRules;

        /*
         * Make the call for the requestRules with the returned id from the Time Off Policy.
         * As well only make the call when the requestRules object actually has values from the form other than id.
         */
        if (requestRules.isDirty()) {
            await requestRules.save();
        }

        // Check if we need to update, create or delete any seniority overrides and set it as a payload
        const overrides = this.advancePolicyForm.timeOffSeniorityOverrides;
        const cacheOverrides = this.advancePolicyForm.timeOffSeniorityOverridesCache;

        const toBeUpdatedOrCreatedSeniorityOverrides = overrides
            .filter((seniorityOverride) => seniorityOverride.isDirty())
            .map((seniorityOverride) => seniorityOverride.toPayload());

        const toBeDeleteSeniorityOverrides = differenceBy(cacheOverrides, overrides, 'id').map((seniorityOverride) =>
            seniorityOverride.toPayload({ delete: true })
        );

        this.timeOffPolicy.timeOffSeniorityOverridesPayload = [
            ...toBeDeleteSeniorityOverrides,
            ...toBeUpdatedOrCreatedSeniorityOverrides,
        ];
    }

    submitAndValidate(form: BaseForm): boolean {
        if (form) {
            form.submit();
            return form.valid;
        }
        return true;
    }

    onCancel(): void {
        this.redirect();
    }

    redirect(): void {
        this.router.navigate(['/time-off/v3/types-and-policies'], { relativeTo: this.route });
    }

    async loadTimeOffTypes(): Promise<void> {
        try {
            this.isLoading = true;
            const [timeOffTypes] = await TimeOffType.all();
            this.timeOffTypes = timeOffTypes;
            this.timeOffTypes = timeOffTypes;
            this.timeOffPolicy.timeOffType = this.timeOffTypes.find(
                (timeOffType) => timeOffType.id === this.timeOffPolicy.timeOffTypeId
            );
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    async loadTimeOffApprovalFlows(): Promise<void> {
        try {
            this.isLoading = true;
            const [timeOffApprovalFlows] = await TimeOffApprovalFlow.get();
            this.timeOffApprovalFlows = timeOffApprovalFlows;
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    showAdvanceSetting(): boolean {
        // If the user has not selected unlimited as the accrual and if the user has selected a time off type then show the advance settings.
        return !!(!this.timeOffPolicy.isUnlimited && this.timeOffPolicy.timeOffTypeId);
    }
}
